<script setup>
import {ref, computed} from 'vue'
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {useI18n} from 'vue-i18n';
import Button from '@/components/forms/Button.vue';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.min.css';
import {Czech} from 'flatpickr/dist/l10n/cs.js';
import {Slovak} from 'flatpickr/dist/l10n/sk.js';

const {t, locale} = useI18n()
const isOpen = ref(true)

const props = defineProps({
  fromDate: {type: String, default: ''},
  toDate: {type: String, default: ''},
  // Nejstarší povolené datum (Y-m-d) - typicky datum první investice z API (first_position_date).
  // Pokud není předané, datepicker spodní hranici neomezuje.
  minDate: {type: String, default: null},
});

const emit = defineEmits(['confirm', 'cancel']);

const localFromDate = ref(props.fromDate);
const localToDate = ref(props.toDate);

const localeMap = {cs: Czech, sk: Slovak};

/**
 * Společná konfigurace obou datepickerů.
 * Hodnota v modelu zůstává ve formátu Y-m-d (tak ji očekává API), uživateli se ale
 * přes altInput zobrazuje česko/slovenský formát d.m.Y.
 */
const baseFlatpickrConfig = computed(() => ({
  dateFormat: 'Y-m-d',
  altInput: true,
  altFormat: 'd.m.Y',
  // altInput je nový <input>, třídu z komponenty nepřebírá - musíme ji nastavit explicitně
  altInputClass: 'form-control',
  disableMobile: true,
  locale: localeMap[locale.value] ?? Czech,
}));

// "Od" nesmí být před první investicí ani za zvoleným "Do".
// vue-flatpickr-component sleduje změny configu, takže se hranice přenastaví hned po výběru druhého data.
const fromFlatpickrConfig = computed(() => ({
  ...baseFlatpickrConfig.value,
  minDate: props.minDate || null,
  maxDate: localToDate.value || 'today',
}));

// "Do" nesmí být před zvoleným "Od" (případně před první investicí) ani v budoucnosti.
const toFlatpickrConfig = computed(() => ({
  ...baseFlatpickrConfig.value,
  minDate: localFromDate.value || props.minDate || null,
  maxDate: 'today',
}));

const closeModalWith = (value) => {
  isOpen.value = false;
  setTimeout(() => {
    if (value === 'confirm') {
      emit('confirm', {from: localFromDate.value, to: localToDate.value});
    } else {
      emit('cancel');
    }
  }, 300);
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="modal d-block" tabindex="-1" role="dialog">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered" style="z-index: 1055" role="document">
        <div class="modal-content">
          <DialogPanel>
            <div class="modal-header">
              <DialogTitle as="h5" class="modal-title">
                {{ t('dateRangeModal.title', 'Výběr období') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>

            <div class="modal-body">
              <div class="row">
                <div class="col-6">
                  <div class="form-group">
                    <label class="form-label">{{ t('dateRangeModal.from', 'Od') }}</label>
                    <flat-pickr v-model="localFromDate" :config="fromFlatpickrConfig" class="form-control" />
                  </div>
                </div>
                <div class="col-6">
                  <div class="form-group">
                    <label class="form-label">{{ t('dateRangeModal.to', 'Do') }}</label>
                    <flat-pickr v-model="localToDate" :config="toFlatpickrConfig" class="form-control" />
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">
                {{ t('dateRangeModal.cancel', 'Zrušit') }}
              </button>
              <Button :label="t('dateRangeModal.confirm', 'Potvrdit')" @click="closeModalWith('confirm')"/>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>